import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { Field, NativeSelect } from "@/components/common";
import { MaterialPicker } from "@/pages/quotes/MaterialPicker";
import { unitOptions, defaultUnit } from "./StockEntry";

export function TransferDialog({ inv, onClose, onDone }) {
  const whs = inv.warehouses;
  const [f, setF] = useState({ item_id: "", from: whs[0]?.id, to: whs[1]?.id || "", qty: "", unit: "uom", piece_id: "", note: "" });
  const item = inv.items.find((i) => i.id === f.item_id);
  const units = unitOptions(item);
  const pieces = (item?.pieces || []).filter((p) => p.warehouse_id === f.from && !p.loose);
  const have = item ? +(item.by_warehouse[f.from] ?? 0).toFixed(4) : 0;
  const ok = item && f.from && f.to && f.from !== f.to && (f.piece_id || Number(f.qty) > 0);
  const submit = async () => {
    const body = { item_id: f.item_id, from_warehouse_id: f.from, to_warehouse_id: f.to, note: f.note, ...(f.piece_id ? { piece_id: f.piece_id } : { qty: Number(f.qty), unit: units ? f.unit : "uom" }) };
    if (await act(() => api.post("/inventory/transfer", body), `Moved ${item.name}`)) { onDone(); onClose(); }
  };
  const whOpts = whs.map((w) => ({ value: w.id, label: w.label || w.name }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="transfer-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Move stock between warehouses</DialogTitle>
          <DialogDescription>Total stock and value stay the same. No purchase, expense or journal is created.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Item"><MaterialPicker testId="transfer-item-picker" mats={inv.items} value={f.item_id} keyOf={(i) => i.id} placeholder="Search item"
            onChange={(v) => setF({ ...f, item_id: v, unit: defaultUnit(inv.items.find((i) => i.id === v)), piece_id: "" })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="From"><NativeSelect testId="transfer-from-select" value={f.from} onChange={(v) => setF({ ...f, from: v, piece_id: "" })} options={whOpts} /></Field>
            <Field label="To"><NativeSelect testId="transfer-to-select" value={f.to} onChange={(v) => setF({ ...f, to: v })} options={whOpts} /></Field>
          </div>
          {item && <p className="text-xs text-slate-600" data-testid="transfer-available">In the from warehouse: {have} {item.uom}</p>}
          {pieces.length > 0 && <Field label="What to move"><NativeSelect testId="transfer-piece-select" value={f.piece_id} onChange={(v) => setF({ ...f, piece_id: v })}
            options={[{ value: "", label: "A quantity" }, ...pieces.map((p) => ({ value: p.id, label: `${p.label || `part ${item.uom}`}${p.left ? ` \u2014 ${p.left}` : ""}` }))]} /></Field>}
          {!f.piece_id && <div className="grid grid-cols-2 gap-2">
            <Field label="Quantity"><Input type="number" data-testid="transfer-qty-input" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} /></Field>
            {units ? <Field label="Count in"><NativeSelect testId="transfer-unit-select" value={f.unit} onChange={(v) => setF({ ...f, unit: v })} options={units} /></Field>
              : <Field label="Unit"><Input disabled value={item?.uom || ""} /></Field>}
          </div>}
          <Field label="Note (optional)"><Input data-testid="transfer-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
          {f.from && f.from === f.to && <p className="text-xs text-rose-600">Choose two different warehouses.</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="transfer-submit-button" disabled={!ok} onClick={submit}>Move stock</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
